import { searchCopy } from "./copy/workspace";
import type { SearchResult } from "./types";
import { escapeHtml } from "./utils";

export type ConfidenceBand = "high" | "medium" | "low";

export function confidenceBand(confidence: number): ConfidenceBand {
  if (confidence >= 0.75) return "high";
  if (confidence >= 0.45) return "medium";
  return "low";
}

function confidenceLabel(band: ConfidenceBand): string {
  return band === "high" ? searchCopy.confidenceHigh
    : band === "medium" ? searchCopy.confidenceMedium
      : searchCopy.confidenceLow;
}

export function searchResultCardMarkup(result: SearchResult): string {
  const band = confidenceBand(result.confidence);
  const percentage = Math.round(Math.min(1, Math.max(0, result.confidence)) * 100);
  const clauseNumber = escapeHtml(result.clauseNumber);
  const pageReference = result.pageReference === null ? "" : ` <span class="result-page-reference">${escapeHtml(result.pageReference)}</span>`;
  const body = result.jimRead === null
    ? `<p class="result-pointer">${searchCopy.noReadPointer.replace("{clauseNumber}", clauseNumber)}</p>`
    : `<p>${escapeHtml(result.jimRead)}</p>`;

  return `
    <article class="jim-read-card search-result-card ${band === "low" ? "low-confidence" : ""}">
      <div class="result-meta">
        <span class="result-clause-reference">${clauseNumber}${pageReference}</span>
        <span class="result-section">${escapeHtml(result.section)}</span>
        <span class="confidence-meter confidence-${band}" aria-label="${confidenceLabel(band)}">
          <span class="confidence-meter-track"><span class="confidence-meter-fill" style="width: ${percentage}%"></span></span>
          <span>${confidenceLabel(band)}</span>
        </span>
      </div>
      <h2>${escapeHtml(result.title)}</h2>
      ${band === "low" ? `<p class="low-confidence-hedge">${searchCopy.lowConfidenceHedge}</p>` : ""}
      ${body}
      <p class="match-reason">${searchCopy.matchReasonLabel} ${escapeHtml(result.matchReason)}</p>
      <div class="result-actions">
        <div class="result-feedback" role="group" aria-label="${searchCopy.feedbackGroupLabel}">
          <button type="button" class="secondary result-feedback-button" data-feedback-verdict="up" data-clause-id="${escapeHtml(result.clauseId)}" aria-label="${searchCopy.feedbackUpLabel}" aria-pressed="false">👍</button>
          <button type="button" class="secondary result-feedback-button" data-feedback-verdict="down" data-clause-id="${escapeHtml(result.clauseId)}" aria-label="${searchCopy.feedbackDownLabel}" aria-pressed="false">👎</button>
        </div>
        <button type="button" class="secondary open-clause-button" data-clause-id="${escapeHtml(result.clauseId)}">${searchCopy.openClauseLabel}</button><button type="button" class="secondary save-result-button" data-clause-number="${clauseNumber}" data-result-title="${escapeHtml(result.title)}">${searchCopy.saveClauseLabel}</button>
      </div>
    </article>
  `;
}

export function initialSearchResultsMarkup(): string {
  return `<div class="empty-state search-empty-state"><h2>${searchCopy.initialResultsHeading}</h2><p>${searchCopy.initialResultsBody}</p></div>`;
}

export function noSearchResultsMarkup(message?: string): string {
  return `<div class="empty-state search-empty-state"><h2>${escapeHtml(message || searchCopy.noResultsFallback)}</h2></div>`;
}
